import type { Theme } from "@/components/ThemeProvider/ThemeProvider";
import type { KeyPaths } from "@/utils/types/KeyPaths";

type ColorTokenType = {
  color: {
    background: {
      accent: {
        primary: {
          rest: string;
          hover: string;
          active: string;
          focus: string;
        };
        secondary: {
          rest: string;
          hover: string;
          active: string;
          focus: string;
        };
        tertiary: {
          rest: string;
          hover: string;
          active: string;
          focus: string;
        };
        inverted: {
          rest: string;
          hover: string;
          active: string;
          focus: string;
        };
        disabled: { rest: string };
      };
      surface: {
        primary: string;
        secondary: string;
        tertiary: string;
        neutral: string;
        contrastInverted: string;
        disabled: string;
      };
      brand: {
        primary: string;
        secondary: string;
        tertiary: string;
      };
      success: {
        primary: string;
        secondary: string;
        tertiary: string;
      };
      danger: {
        primary: string;
        secondary: string;
        tertiary: string;
      };
      information: {
        primary: string;
        secondary: string;
        tertiary: string;
      };
      warning: {
        primary: string;
        secondary: string;
        tertiary: string;
      };
      interaction: {
        hover: string;
        active: string;
        focus: string;
      };
    };
    foreground: {
      text: {
        primary: string;
        secondary: string;
        tertiary: string;
        contrast: string;
        inverted: string;
        accent: string;
        success: string;
        danger: string;
        warning: string;
        information: string;
      };
      icon: {
        primary: string;
        secondary: string;
        tertiary: string;
        inverted: string;
      };
    };
    border: {
      layout: {
        subtle: string;
        bold: string;
        custom: string;
      };
      input: {
        rest: string;
        hover: string;
        active: string;
        focus: string;
      };
    };
  };
};

type RadiiTokenType = {
  radii: {
    circular: string;
    none: string;
    xs: string;
    s: string;
    m: string;
    l: string;
    xl: string;
  };
};

type SizeTokenType = {
  size: {
    xs: string;
    s: string;
    m: string;
    l: string;
    xl: string;
  };
};

type ShadowTokenType = {
  shadow: {
    xs: string;
    s: string;
    m: string;
    l: string;
    xl: string;
    inner: string;
  };
};

type SpacingTokenType = {
  spacing: {
    none: string;
    xxs: string;
    xs: string;
    s: string;
    m: string;
    l: string;
    xl: string;
    xxl: string;
  };
};

type TypographyTokenType = {
  typography: {
    title: { s: string; m: string; l: string };
    body: {
      bolder: {
        s: string;
        m: string;
        l: string;
      };
      regular: {
        s: string;
        m: string;
        l: string;
      };
    };
    caption: {
      s: string;
      m: string;
      l: string;
    };
  };
};
type ColorType = KeyPaths<ColorTokenType>;
type RadiiType = KeyPaths<RadiiTokenType>;
type ShadowType = KeyPaths<ShadowTokenType>;
type SizeType = KeyPaths<SizeTokenType>;
type SpacingType = KeyPaths<SpacingTokenType>;
type TypographyType = KeyPaths<TypographyTokenType>;

export type CppThemeTypes = ColorType | RadiiType | ShadowType | SizeType | SpacingType | TypographyType;

export type CppThemeMapType = { [k: string]: CppThemeTypes };
export const get = (theme: Theme | undefined, token: CppThemeTypes, fallback: string | null) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const key = token && token.split ? token.split(".") : [token];
  for (let i = 0; i < key.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    theme = theme ? theme[key[i]] : undefined;
  }
  return theme === undefined ? fallback : theme;
};
export const themeGet =
  (token: CppThemeTypes, fallback = null) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: any) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
    get(props.theme, token, fallback);
export default themeGet;
